import React, { useState } from "react";
import { addDays, addYears, format, isAfter, isBefore, startOfDay, subDays } from "date-fns";
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from "react-icons/md";
import { AiOutlineCalendar } from "react-icons/ai";
import useMediaQuery from "@mui/material/useMediaQuery";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";
import EAButton from "../Buttons/EAButton";
import EAIconButton from "../Buttons/EAIconButton";
import EACalendarModal from "../CalendarModal/EACalendarModal";
import "./EAScheduleDatePicker.scss";

interface IProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  requireConfirmation?: boolean;
  unavailableDays?: Date[];
  minDate?: Date;
  maxDate?: Date;
}

const EAScheduleDatePicker: React.FC<IProps> = ({
  selectedDate,
  onDateChange,
  requireConfirmation = false,
  unavailableDays,
  minDate,
  maxDate,
}) => {
  const [openCalendar, setOpenCalendar] = useState(false);
  const isMobile = useMediaQuery("(max-width: 980px)");
  const locale = useLocaleByLanguage();

  const effectiveMinDate = startOfDay(minDate ?? new Date());
  const effectiveMaxDate = startOfDay(maxDate ?? addYears(new Date(), 1));
  const selectedDay = startOfDay(selectedDate);

  const canGoPrevious = isAfter(selectedDay, effectiveMinDate);
  const canGoNext = isBefore(selectedDay, effectiveMaxDate);

  const getFormattedDate = () => {
    if (isMobile) {
      return format(selectedDate, "dd MMM", { locale });
    }
    return format(selectedDate, "dd.MM.yyyy", { locale });
  };

  const handleOpenCalendar = () => {
    setOpenCalendar(true);
  };

  const handleCloseCalendar = () => {
    setOpenCalendar(false);
  };

  const handleDateChange = (date: Date) => {
    onDateChange(date);
    setOpenCalendar(false);
  };

  const handlePreviousDay = () => {
    if (!canGoPrevious) return;
    onDateChange(subDays(selectedDate, 1));
  };

  const handleNextDay = () => {
    if (!canGoNext) return;
    onDateChange(addDays(selectedDate, 1));
  };

  return (
    <>
      <div className="ea-schedule-date-picker">
        {canGoPrevious && (
          <EAIconButton
            title="Poprzedni dzień"
            icon={<MdOutlineKeyboardArrowLeft />}
            variant="outlined"
            onClick={handlePreviousDay}
          />
        )}

        <EAButton
          title={getFormattedDate()}
          endIcon={<AiOutlineCalendar />}
          onClick={handleOpenCalendar}
          variant="outlined"
        />

        <EAIconButton
          title="Następny dzień"
          icon={<MdOutlineKeyboardArrowRight />}
          variant="outlined"
          onClick={handleNextDay}
          disabled={!canGoNext}
        />
      </div>

      {openCalendar && (
        <EACalendarModal
          onClose={handleCloseCalendar}
          onDatePick={handleDateChange}
          date={selectedDate}
          requireConfirmation={requireConfirmation}
          unavailableDays={unavailableDays}
          fromDate={effectiveMinDate}
          toDate={effectiveMaxDate}
        />
      )}
    </>
  );
};

export default EAScheduleDatePicker;
